import React from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Shield, User, LogOut } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <Layout title="Settings" subtitle="account & workspace">
      <section className="mb-8">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-2">
          Account
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
          Workspace settings.
        </h2>
        <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
          Your identity is shared across every module. Sign out here to end
          your session across the entire ecosystem.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-md border border-slate-200 bg-white p-6" data-testid="settings-profile-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <User className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">
                Profile
              </div>
              <div className="text-xs text-slate-500">Identity details</div>
            </div>
          </div>
          <dl className="space-y-3 pt-2">
            <Row label="Name" value={user?.name} testid="settings-field-name" />
            <Row label="Email" value={user?.email} testid="settings-field-email" />
            <Row label="Role" value={user?.role} testid="settings-field-role" />
            <Row label="User ID" value={user?.id} mono testid="settings-field-id" />
          </dl>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-6" data-testid="settings-security-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <Shield className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">
                Security
              </div>
              <div className="text-xs text-slate-500">Session & tokens</div>
            </div>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Your session uses short-lived access tokens (15 min) and refresh
            tokens (7 days) stored in secure httpOnly cookies. Sign out below
            to clear them immediately.
          </p>
          <Button
            onClick={handleLogout}
            className="mt-5 bg-red-600 hover:bg-red-700 text-white"
            data-testid="settings-logout-button"
          >
            <LogOut className="mr-1.5 h-4 w-4" /> Sign out
          </Button>
        </div>

        <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white p-6" data-testid="settings-ecosystem-card">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            Ecosystem
          </div>
          <div className="font-display text-lg font-semibold text-slate-900 mb-4">
            Merged modules
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <li className="rounded-md border border-indigo-200 bg-indigo-50/60 p-4">
              <div className="h-1.5 w-1.5 rounded-full bg-indigo-500 mb-2" />
              <div className="font-mono text-[10px] tracking-widest uppercase text-indigo-700 mb-1">
                custom-app-forge-14
              </div>
              <div className="font-medium text-slate-900">App Forge</div>
            </li>
            <li className="rounded-md border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 mb-2" />
              <div className="font-mono text-[10px] tracking-widest uppercase text-emerald-700 mb-1">
                calendar-maps-pod
              </div>
              <div className="font-medium text-slate-900">Calendar · Maps</div>
            </li>
            <li className="rounded-md border border-amber-200 bg-amber-50/60 p-4">
              <div className="h-1.5 w-1.5 rounded-full bg-amber-500 mb-2" />
              <div className="font-mono text-[10px] tracking-widest uppercase text-amber-700 mb-1">
                quran-connections
              </div>
              <div className="font-medium text-slate-900">Quran Connections</div>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0">
      <dt className="font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-1 shrink-0">
        {label}
      </dt>
      <dd
        data-testid={testid}
        className={`text-sm text-slate-900 text-right ${mono ? "font-mono text-xs" : ""} truncate`}
      >
        {value || "—"}
      </dd>
    </div>
  );
}
